import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-cream-50 p-4 font-sans">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center border-t-8 border-maroon-900 relative overflow-hidden">
            {/* Background Decor */}
            <div className="absolute top-0 right-0 -mr-10 -mt-10 w-32 h-32 bg-gold-100 rounded-full opacity-50 blur-xl pointer-events-none"></div>
            
            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 relative z-10 animate-pulse-slow">
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
            
            <h1 className="text-2xl font-serif font-bold text-maroon-900 mb-2 relative z-10">Something went wrong</h1>
            <p className="text-gray-600 mb-8 text-sm relative z-10">
              We encountered an unexpected issue. Please try refreshing the page.
            </p>

            {/* Error Message for Debugging (Visible in all envs based on user request for visibility) */}
            {this.state.error && (
              <div className="bg-gray-50 border border-gray-200 p-3 rounded text-left mb-6 overflow-auto max-h-32 text-xs text-red-800 font-mono shadow-inner relative z-10">
                <p className="font-bold border-b border-gray-200 pb-1 mb-1">Error Details:</p>
                {this.state.error.toString()}
              </div>
            )}

            <div className="space-y-3 relative z-10">
              <button
                onClick={() => window.location.reload()}
                className="w-full inline-flex items-center justify-center px-6 py-3 bg-gold-500 text-maroon-950 font-bold rounded-lg hover:bg-gold-400 transition-colors uppercase tracking-widest text-sm shadow-md"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Page
              </button>
              
              <button
                onClick={() => window.location.href = '/'}
                className="w-full inline-flex items-center justify-center px-6 py-3 bg-white text-maroon-900 border border-gray-200 font-bold rounded-lg hover:bg-gray-50 transition-colors uppercase tracking-widest text-sm"
              >
                <Home className="w-4 h-4 mr-2" />
                Return Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}